import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronRight, ClipboardList, FileText, Link2, Loader2, Plus, Users } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { dueLine, fromDeadlineParts, KIND_ORDER, KINDS, kindOf, publishNote, stateOf, toneOf } from "../../lib/portalKinds";
import { pendingText, pendingTone } from "../../lib/portalAgenda";
import DeadlineField from "./DeadlineField";
import AttendanceFields from "./AttendanceFields";
import { parseHours } from "../../lib/portalAttendance";
import { Chip, fieldCls, primaryBtn, secondaryBtn } from "./PortalShell";
import LibraryLessons from "./LibraryLessons";
import { FormattedTextarea } from "./FormattedText";
import { isLibrary } from "../../lib/courseLessons";

/**
 * What a group card holds, one section per kind in the registry: assignments,
 * then material. A participant sees what is asked of them and where they
 * stand; the group's trainer sees everything, gives new ones, and reaches the
 * list of participants.
 */
// `fped`: a Curs FPED cohort — no hours counted toward attendance, no presence or portfolios.
export default function GroupActivities({ groupId, fped = false, invited = true }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(null);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/portal/groups/${groupId}/activities`)).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut încărca temele."));
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  if (!data && !error) {
    return <div className="py-4 text-center text-[#6B7280]"><Loader2 size={18} className="mx-auto animate-spin" /></div>;
  }
  const trainer = data?.role === "trainer";
  // What came from the course library has its own section, by lesson (28.10.2026).
  const fromLibrary = (data?.activities || []).filter(isLibrary);
  const all = (data?.activities || []).filter((a) => kindOf(a) && !isLibrary(a));

  return (
    <>
      {error && <p className="mt-3 text-sm text-[#c0483f]" role="alert">{error}</p>}
      {trainer && <GroupLink groupId={groupId} fped={fped} />}
      {data && fromLibrary.length > 0 && (
        <LibraryLessons activities={fromLibrary} lessons={data.lessons} trainer={trainer} groupId={groupId} />
      )}
      {data && KIND_ORDER.map((kind) => {
        const rows = all.filter((a) => a.kind === kind);
        // A participant is not shown an empty "Materiale" heading, nor an empty
        // "Teme" one when the lessons above already hold the assignments.
        if (!trainer && rows.length === 0 && (kind !== "assignment" || fromLibrary.length > 0)) return null;
        return (
          <Section key={kind} kind={kind} rows={rows} trainer={trainer} adding={adding === kind}
            onAdd={() => setAdding(kind)} onDone={() => { setAdding(null); load(); }} onCancel={() => setAdding(null)}
            groupId={groupId} fped={fped} invited={invited} />
        );
      })}
    </>
  );
}

/** The way to the group's page, with where the group is and how much waits
 * there (06.10.2026): the agenda decides both. */
function GroupLink({ groupId, fped }) {
  const [agenda, setAgenda] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/portal/groups/${groupId}/agenda`).then((r) => { if (live) setAgenda(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [groupId]);
  return (
    <Link to={`/portal/grupa/${groupId}`}
      className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-[#965EA7]/[0.07] px-4 py-3 text-sm font-semibold text-[#7A4986] hover:bg-[#965EA7]/[0.12]"
      data-testid="portal-roster-link">
      <Users size={16} className="shrink-0" />
      <span className="min-w-0">
        Pagina grupei
        <span className="block text-xs font-normal text-[#6B7280]">
          {fped ? "Ce ai de făcut, prezența și cursanții" : "Ce ai de făcut, prezența, cursanții, portofoliile"}
        </span>
      </span>
      <span className="ml-auto flex items-center gap-2">
        {agenda && <Chip>{agenda.stage_label}</Chip>}
        {agenda && <Chip tone={pendingTone(agenda.counts)}>{pendingText(agenda.counts)}</Chip>}
        <ChevronRight size={16} className="shrink-0" />
      </span>
    </Link>
  );
}

// The section's icon and each row's, by kind; a kind without one gets the first.
const ICONS = {
  assignment: { section: ClipboardList, row: FileText },
  resource: { section: BookOpen, row: Link2 },
};

function Section({ kind, rows, trainer, adding, onAdd, onDone, onCancel, groupId, fped, invited }) {
  const def = KINDS[kind];
  const tone = toneOf(kind);
  const icons = ICONS[kind] || ICONS.assignment;
  const SectionIcon = icons.section;
  const RowIcon = icons.row;
  return (
    <section className={`mt-4 rounded-xl border p-4 ${tone.panel}`} aria-labelledby={`portal-section-${kind}-${groupId}`}
      data-testid={`portal-section-${kind}`}>
      <div className="flex items-center gap-3">
        <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${tone.icon}`} aria-hidden="true">
          <SectionIcon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 id={`portal-section-${kind}-${groupId}`} className="flex items-center gap-2 text-[15px] font-bold text-[#373A45]">
            {def.plural}
            {rows.length > 0 && (
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${tone.badge}`}>{rows.length}</span>
            )}
          </h3>
          {def.hint && <p className="text-xs text-[#6B7280]">{trainer ? def.hint.trainer : def.hint.participant}</p>}
        </div>
        {trainer && !adding && (
          <button type="button" onClick={onAdd} className={`${secondaryBtn} shrink-0`} data-testid={`portal-add-${kind}`}>
            <Plus size={14} /> {def.newLabel}
          </button>
        )}
      </div>
      {adding && <NewActivity kind={kind} groupId={groupId} onDone={onDone} onCancel={onCancel} fped={fped} invited={invited} />}

      {rows.length === 0 && !adding && (
        <p className="mt-3 rounded-lg border border-dashed border-[#D1D5DB] bg-white/70 px-3 py-2.5 text-sm text-[#6B7280]">
          {trainer
            ? def.submits ? "Nicio temă încă. Prima pe care o publici apare aici și la cursanți."
              : "Niciun material încă: ghiduri, prezentări, linkuri pentru toată grupa."
            : "Nicio temă deocamdată."}
        </p>
      )}
      {rows.length > 0 && (
        <ul className="mt-3 divide-y divide-[#E5E7EB] overflow-hidden rounded-lg border border-[#E5E7EB] bg-white">
          {rows.map((a) => {
            const st = stateOf(a);
            return (
              <li key={a.id}>
                <Link to={`/portal/tema/${a.id}`}
                  className="group flex items-center gap-3 px-3 py-3 hover:bg-[#F9FAFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#965EA7]"
                  data-testid="portal-activity-row">
                  <RowIcon size={16} className={`shrink-0 ${tone.rowIcon}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#373A45] group-hover:text-[#965EA7]">{a.title}</p>
                    {def.submits && (
                      <p className="text-xs text-[#6B7280]">
                        {dueLine(a.due_at)}
                        {trainer && ` · ${a.submitted_count === 1 ? "o predare" : `${a.submitted_count} predări`}`}
                      </p>
                    )}
                  </div>
                  {trainer && !a.published && <Chip>Nepublicat</Chip>}
                  {!trainer && def.submits && <Chip tone={st.tone}>{st.label}</Chip>}
                  <ChevronRight size={16} className="shrink-0 text-[#9CA3AF]" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function NewActivity({ kind, groupId, onDone, onCancel, fped, invited }) {
  const def = KINDS[kind];
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [due, setDue] = useState({ date: "", time: "" });
  const [att, setAtt] = useState({ counts: false, hours: "", theme: "", component: "practical" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (published) => {
    if (!title.trim()) { setError("Are nevoie de un titlu."); return; }
    setSaving(true);
    setError("");
    try {
      await api.post(`/portal/groups/${groupId}/activities`, {
        kind, title, description, published, ...(def.submits ? { due_at: fromDeadlineParts(due.date, due.time) } : {}),
        counts_for_attendance: att.counts, hours: att.counts ? parseHours(att.hours) || 0 : 0,
        theme: att.theme ? Number(att.theme) : null, component: att.component || "practical",
      });
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut salva."));
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 grid gap-3 rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid={`portal-new-${kind}`}>
      <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
        Titlu
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} className={fieldCls}
          placeholder={def.submits ? "Tema 1: un plan de lecție cu AI" : "Ghid: primii pași în Gemini"} />
      </label>
      <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
        {def.submits ? "Ce au de făcut" : "Descriere"}
        <FormattedTextarea value={description} onChange={setDescription} rows={6} className={fieldCls} />
      </label>
      {def.submits ? (
        <div className="grid gap-1 text-sm font-semibold text-[#373A45]">
          Termen
          <DeadlineField value={due} onChange={setDue} />
          <span className="text-xs font-normal text-[#6B7280]">Ora României. Ce se predă după termen se primește, marcat ca întârziat.</span>
        </div>
      ) : (
        <p className="text-xs text-[#6B7280]">Fișierele și linkurile le adaugi după ce salvezi, din pagina materialului.</p>
      )}
      {!fped && <AttendanceFields value={att} onChange={setAtt} kind={kind} />}
      {error && <p className="text-sm text-[#c0483f]" role="alert">{error}</p>}
      <p className="text-xs text-[#6B7280]" data-testid="portal-publish-note">{publishNote(kind, invited)}</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => save(true)} disabled={saving} className={primaryBtn}>Publică</button>
        <button type="button" onClick={() => save(false)} disabled={saving} className={secondaryBtn}>Salvează nepublicat</button>
        <button type="button" onClick={onCancel} disabled={saving} className="px-3 text-sm font-semibold text-[#6B7280] hover:text-[#373A45]">
          Renunță
        </button>
      </div>
    </div>
  );
}
